/**
 * Performance monitoring utilities for tracking and optimizing application performance
 */

// Store performance marks and measures
const performanceData = {
  marks: {},
  measures: {}
};

/**
 * Start timing a specific operation
 * @param {string} name - Name of the operation to time
 */
export const startMeasure = (name) => {
  if (!window.performance) return;
  
  const markName = `${name}_start`;
  window.performance.mark(markName);
  performanceData.marks[markName] = Date.now();
};

/**
 * End timing a specific operation and log the result
 * @param {string} name - Name of the operation to end timing
 * @param {boolean} log - Whether to log the result to console
 * @returns {number} - Duration in milliseconds
 */
export const endMeasure = (name, log = true) => {
  if (!window.performance) return 0;
  
  const startMark = `${name}_start`;
  const endMark = `${name}_end`;
  const measureName = `${name}_duration`;
  
  window.performance.mark(endMark);
  
  try {
    window.performance.measure(measureName, startMark, endMark);
    const entries = window.performance.getEntriesByName(measureName);
    const duration = entries.length > 0 ? entries[0].duration : 0;
    
    performanceData.measures[name] = duration;
    
    if (log) {
      console.log(`Performance: ${name} took ${duration.toFixed(2)}ms`);
    }
    
    return duration;
  } catch (e) {
    console.error(`Error measuring performance for ${name}:`, e);
    return 0;
  }
};

/**
 * Track component render time using React's useEffect
 * @param {string} componentName - Name of the component
 * @returns {Object} - Object with start and end functions
 */
export const useComponentPerformance = (componentName) => {
  return {
    start: () => startMeasure(`render_${componentName}`),
    end: () => endMeasure(`render_${componentName}`)
  };
};

/**
 * Get all performance measurements
 * @returns {Object} - All performance measurements
 */
export const getAllMeasurements = () => {
  return { ...performanceData.measures };
};

/**
 * Clear all performance measurements
 */
export const clearMeasurements = () => {
  if (!window.performance) return;
  
  window.performance.clearMarks();
  window.performance.clearMeasures();
  
  performanceData.marks = {};
  performanceData.measures = {};
};

/**
 * Track initial page load metrics
 */
export const trackPageLoadMetrics = () => {
  if (!window.performance) return {};
  
  const navigationTiming = window.performance.timing;
  const pageLoadTime = navigationTiming.loadEventEnd - navigationTiming.navigationStart;
  const dnsTime = navigationTiming.domainLookupEnd - navigationTiming.domainLookupStart;
  const tcpTime = navigationTiming.connectEnd - navigationTiming.connectStart;
  const ttfb = navigationTiming.responseStart - navigationTiming.requestStart;
  const domLoadTime = navigationTiming.domComplete - navigationTiming.domLoading;
  
  const metrics = {
    pageLoadTime,
    dnsTime,
    tcpTime,
    ttfb,
    domLoadTime
  };
  
  console.log('Page Load Metrics:', metrics);
  return metrics;
};

// Export a default monitor object for easy access
export default {
  startMeasure,
  endMeasure,
  useComponentPerformance,
  getAllMeasurements,
  clearMeasurements,
  trackPageLoadMetrics
};
